import React, { useState, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import ReactPaginate from 'react-paginate';
import { Tbody, Th, Thead, Tr, Table, Td } from 'react-super-responsive-table';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { Components } from '.generated/templates/Feature.Components.model';
import { Data } from '.generated/templates/Feature.Data.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import TableHeading from 'components/authorable/Ritn/ParticipatingCenter/Icon/TableHeading';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { ComponentFolders, isTableColumnFolder, isTableRowFolder, isTable } from 'lib/sitecore';
import { logWarning } from 'lib/util/errors';

type TableProbs = Sitecore.Override.ComponentBase &
  Components.General.Table.Fields.Table & {
    fields?: {
      root: ComponentFolders.General.Table.Table;
    };
  };

type Column = Data.Table.Fields.Column & { id: string };
type Row = Data.Table.Fields.Row & { id: string };
type ColumnNames = ComponentFolders.General.Table.TableColumnNames;

type ColumnSortIndicatorProps = {
  columName: string;
  isAscending: boolean;
};
type TableContentProps = {
  columnFolder: ComponentFolders.General.Table.TableColumnFolder;
  rowFolder: ComponentFolders.General.Table.TableRowFolder;
};

const getTableContent = (
  table: ComponentFolders.General.Table.Table | undefined
): TableContentProps => {
  const ret = {} as Partial<TableContentProps>;
  if (isTable(table)) {
    table.children?.forEach((child) => {
      if (isTableColumnFolder(child)) {
        if (ret.columnFolder) {
          logWarning('Table has multiple column folders - skipping', { ret, child });
        } else {
          ret.columnFolder = child;
        }
      } else if (isTableRowFolder(child)) {
        if (ret.rowFolder) {
          logWarning('Table has multiple row folders - skipping', { ret, child });
        } else {
          ret.rowFolder = child;
        }
      }
    });
  }
  if (!ret.columnFolder || !ret.rowFolder) {
    logWarning('Table is missing required column or row folder', { ret });
  }
  return ret as Required<TableContentProps>;
};

/**
 * Maps a column item to a table header component
 */
const MapColumnToTableHeader = ({
  column,
  index,
  sortTable,
  sortIndicator,
}: {
  column: Column;
  index: number;
  sortTable: (columnKey: string, isAscending: boolean) => number;
  sortIndicator: ColumnSortIndicatorProps;
}): React.JSX.Element => {
  const { fields } = column;
  const { columnLabel, sortable, columnSpan } = fields ?? {
    columnLabel: { value: 'Column' },
    sortable: { value: false },
    columnSpan: { value: 1 },
  };
  const thClass = classnames('p-2.5', 'border', 'border border-gray-quartz');
  return (
    <Th className={thClass} colSpan={columnSpan.value}>
      <TableHeading
        label={columnLabel.value}
        sortTable={sortTable}
        sortIndicator={sortIndicator}
        showSortIcon={sortable.value?.toString() === 'true'}
        columnKey={`column${index + 1}`}
      />
    </Th>
  );
};

/**
 * Maps a row item to a table row component
 */
const MapRowToTableRow = ({
  row,
  columns,
}: {
  row: Row;
  columns?: Column[];
}): React.JSX.Element => {
  const tdClass = classnames('p-2.5', 'border', 'border-gray-quartz');
  return (
    <Tr
      key={row.id}
      className={classnames(
        'p-2.5',
        'even:bg-gray-whisper',
        'mb-2',
        'sm:mb-0',
        'border',
        'border-gray-quartz'
      )}
    >
      {(columns ?? []).map((column, i) => {
        const columnFieldName = `column${i + 1}`;
        const columnKey = column.id ?? columnFieldName;
        const colspan = column.fields?.columnSpan?.value ?? 1;
        const columnField =
          row.fields && columnFieldName in row.fields
            ? (row.fields as { [key: string]: Field<string> })[columnFieldName]
            : undefined;

        return (
          <Td key={columnKey} className={tdClass} colSpan={colspan}>
            <RichTextA11yWrapper
              className={classnames('flex', 'basis-1/2', 'rte')}
              field={columnField}
              editable
            />
          </Td>
        );
      })}
    </Tr>
  );
};

/**
 * Retrieves a table sorter for a given column
 * @param {ColumnNames} columns The column to retrieve
 * @param {boolean} isAscending If true an ascending sort is used.
 */
const getTableSorterForColumn =
  (column: ColumnNames, isAscending: boolean) =>
  (a: Data.Table.Fields.Row, b: Data.Table.Fields.Row) => {
    const aValueString = a.fields?.[column]?.value ?? '';
    const bValueString = b.fields?.[column]?.value ?? '';

    // Parse HTML content and get text content
    const aValue =
      new DOMParser().parseFromString(aValueString, 'text/html').body.textContent ?? '';
    const bValue =
      new DOMParser().parseFromString(bValueString, 'text/html').body.textContent ?? '';

    // Perform locale compare for sorting
    const comparison = aValue.localeCompare(bValue, undefined, { numeric: true });

    // Return the comparison result considering the sort order
    return isAscending ? comparison : -comparison;
  };

/**
 * Returns a page of rows given an item offset and page size
 */
const paginateRows = ({
  rows,
  pageSize,
  itemOffset = 0,
}: {
  rows?: Row[];
  itemOffset?: number;
  pageSize: number;
}) => {
  const endOffset = (itemOffset ?? 0) + pageSize;
  const copyOfRows = [...(rows ?? [])];
  return copyOfRows.slice(itemOffset, endOffset);
};

const TableComponent = ({ fields: rootProps }: TableProbs): JSX.Element => {
  // Setup state hooks
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [sortIndicator, setSortIndicator] = useState<ColumnSortIndicatorProps>({
    columName: '',
    isAscending: false,
  });
  const isExperienceEditor = useExperienceEditor();
  // These values are needed to determine initial component state
  const { columnFolder, rowFolder } = getTableContent(rootProps?.root);
  const { paginationSize } = rootProps?.root?.fields ?? {};
  const pageSize = isExperienceEditor ? Number.MAX_SAFE_INTEGER : paginationSize?.value ?? 10;
  // Back to hook setup
  const [tableData, setTableData] = useState<Row[]>(rowFolder?.children ?? []);
  // Never display pagination in experience editor - it messes with editing.
  const displayPagination = !isExperienceEditor && tableData.length > pageSize;
  /**
   * Sorts table rows for a given column and type.
   * @param {string} columnKey A key identifying the column to sort by.
   * @param {boolean} isAscending true to sort in ascending order, false for descending.
   * @returns {1} Always returns 1
   */
  const sortTableCallback = useCallback(
    (columnKey: string, isAscending: boolean) => {
      if (isExperienceEditor) {
        return 1;
      }
      setTableData((prevCenterList) => {
        const sortedCenterList = [...prevCenterList];
        const sortFn = getTableSorterForColumn(columnKey as ColumnNames, isAscending);
        if (sortFn) {
          sortedCenterList.sort(sortFn);
        }
        setSortIndicator({
          columName: columnKey,
          isAscending: isAscending,
        });
        return sortedCenterList;
      });
      return 1;
    },
    [isExperienceEditor]
  );
  /**
   * Processes a page change click event
   */
  const handlePageClickCallback = useCallback(
    (event: { selected: number }) => {
      const newOffset = (event.selected * pageSize) % tableData.length;
      setItemOffset(newOffset);
    },
    [tableData, pageSize]
  );

  const { children: columnItems } = columnFolder ?? {};

  /**
   * Memoize column headers so we don't have to recompute every render
   */
  const buildTableHeaders = useMemo(
    () =>
      columnItems?.map((column, index) => (
        <MapColumnToTableHeader
          key={column.id}
          column={column}
          index={index}
          sortIndicator={sortIndicator}
          sortTable={sortTableCallback}
        />
      )),
    [columnItems, sortIndicator, sortTableCallback]
  );
  if (!columnFolder || !rowFolder) {
    return <MissingDataSource />;
  }
  let currentItems: Row[];
  if (!isExperienceEditor) {
    // Update row state with current page
    currentItems = paginateRows({ rows: tableData, itemOffset, pageSize });
    // Update number of available pages
    const newPageCount = Math.ceil(tableData.length / pageSize);
    if (newPageCount !== pageCount) {
      setPageCount(newPageCount);
    }
  } else {
    currentItems = tableData;
  }

  return (
    <div data-component="authorable/general/Table">
      <Table className="hide-header-in-mobile">
        <Thead>
          <Tr className={classnames('text-left', 'font-bold')}>{buildTableHeaders}</Tr>
        </Thead>
        <Tbody>
          {currentItems.map((row) => (
            <MapRowToTableRow row={row} columns={columnItems} key={row.id} />
          ))}
        </Tbody>
      </Table>

      {displayPagination && (
        <div className={classnames('mt-3', 'table-pagination')}>
          <ReactPaginate
            nextLabel=""
            onPageChange={handlePageClickCallback}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={pageCount}
            previousLabel=""
            pageClassName="table-pagination-item text-blue-morning text-body underline"
            pageLinkClassName="pagination-link"
            previousClassName="page-item"
            previousLinkClassName="pagination-prev pagination-link"
            nextClassName="page-item"
            nextLinkClassName="pagination-next pagination-link"
            breakLabel="..."
            breakClassName="table-pagination-item"
            breakLinkClassName="pagination-link"
            containerClassName="pagination-list"
            activeClassName="no-underline text-blue-night"
          />
        </div>
      )}
    </div>
  );
};

export default TableComponent;
